/**
 * Created by Jerker Nord on 2021-10-19.
 */
import {NolimitPlugin} from "@nolimitcity/slot-launcher/bin/NolimitPlugin";
import {TrueGrit} from "../TrueGrit";
import {TextureHelper} from "../utils/TextureHelper";
import {DropShadowFilter, MotionBlurFilter} from "pixi-filters";
import {TrueGritGameAssets} from "../TrueGritGameAssets";
import {gsap} from "gsap";

export interface ITextureContainer {
    textureName:string,
    container:PIXI.Container,
    extraWidth:number,
    filters:PIXI.Filter[]
}

export class ResourceCreator {

    private static LOW_SYMBOL_NAMES:string[] = ["L1", "L2", "L3", "L4", "L5"];
    private static MID_SYMBOL_NAMES:string[] = ["M1", "M2", "M3", "M4", "M5"];
    private static GRITA_SYMBOL_NAMES:string[] = ["M0", "M0_2", "M0_3"];
    private static FEATURE_SYMBOL_NAMES:string[] = ["M", "M_infectious", "Z", "W2", "T", "T2", "X"];
    private static LOW_SYMBOL_SPLIT_BACKGROUND:string = "Lbg_split";
    private static MID_SYMBOL_FRAME:string = "midSymParts/Mframe";
    private static WILD_SYMBOL_FRAME:string = "W/Wframe";
    private static GRITA_SYMBOL_FRAME:string = "_frame";
    private static MID_SYMBOL_BACKGROUND:string = "_bg";
    private static GRITA_SYMBOL_BACKGROUND:string = "_bg";
    private static SYMBOL_CHAR:string = "_char";
    private static GRITA_SYMBOL_CHAR:string = "_char";
    private static MID_SYMBOL_SPLIT_LINE:string = "midSymParts/midSymSplitLine";
    private static lowSymShadowFilter:DropShadowFilter = new DropShadowFilter({
        distance : 3,
        color : 0x000000,
        blur : 0
    });
    private static verticalSpinBlurFilter:MotionBlurFilter = new MotionBlurFilter([0, 20], 25, 0);
    private static horizontalSpinBlurFilter:MotionBlurFilter = new MotionBlurFilter([20, 0], 25, 0);
    private static textureContainers:ITextureContainer[] = [];

    constructor() {
    }

    public static createResources():Promise<NolimitPlugin> {
        return new Promise<any>((resolve, reject) => {
            this.createWildNoSplitTextures();
            this.createWildSplit2Textures();
            this.createWildSplit3Textures();
            this.createWildSplit4Textures();
            for(let i:number = 0; i < ResourceCreator.MID_SYMBOL_NAMES.length; i++) {
                this.createMidSymNoSplit(ResourceCreator.MID_SYMBOL_NAMES[i]);
                this.createMidSymSplit2(ResourceCreator.MID_SYMBOL_NAMES[i]);
                this.createMidSymSplit3(ResourceCreator.MID_SYMBOL_NAMES[i]);
                this.createMidSymSplit4(ResourceCreator.MID_SYMBOL_NAMES[i]);
                this.createMidSymNoSplit(ResourceCreator.MID_SYMBOL_NAMES[i], true);
                this.createMidSymSplit2(ResourceCreator.MID_SYMBOL_NAMES[i], true);
                this.createMidSymSplit3(ResourceCreator.MID_SYMBOL_NAMES[i], true);
                this.createMidSymSplit4(ResourceCreator.MID_SYMBOL_NAMES[i], true);
                this.createMidSymNoSplit(ResourceCreator.MID_SYMBOL_NAMES[i], true, true);
                this.createMidSymSplit2(ResourceCreator.MID_SYMBOL_NAMES[i], true, true);
                this.createMidSymSplit3(ResourceCreator.MID_SYMBOL_NAMES[i], true, true);
                this.createMidSymSplit4(ResourceCreator.MID_SYMBOL_NAMES[i], true, true);
                this.createMidSymWildTransform(ResourceCreator.MID_SYMBOL_NAMES[i]);
                this.createMidSymSplit2WildTransform(ResourceCreator.MID_SYMBOL_NAMES[i]);
                this.createMidSymSplit3WildTransform(ResourceCreator.MID_SYMBOL_NAMES[i]);
                this.createMidSymSplit4WildTransform(ResourceCreator.MID_SYMBOL_NAMES[i]);
            }
            for(let i:number = 0; i < ResourceCreator.LOW_SYMBOL_NAMES.length; i++) {
                this.createLowSymNoSplit(ResourceCreator.LOW_SYMBOL_NAMES[i]);
                this.createLowSymSplit2(ResourceCreator.LOW_SYMBOL_NAMES[i]);
                this.createLowSymSplit3(ResourceCreator.LOW_SYMBOL_NAMES[i]);
                this.createLowSymSplit4(ResourceCreator.LOW_SYMBOL_NAMES[i]);
                this.createLowSymNoSplit(ResourceCreator.LOW_SYMBOL_NAMES[i], true);
                this.createLowSymSplit2(ResourceCreator.LOW_SYMBOL_NAMES[i], true);
                this.createLowSymSplit3(ResourceCreator.LOW_SYMBOL_NAMES[i], true);
                this.createLowSymSplit4(ResourceCreator.LOW_SYMBOL_NAMES[i], true);
                this.createLowSymNoSplit(ResourceCreator.LOW_SYMBOL_NAMES[i], true, true);
                this.createLowSymSplit2(ResourceCreator.LOW_SYMBOL_NAMES[i], true, true);
                this.createLowSymSplit3(ResourceCreator.LOW_SYMBOL_NAMES[i], true, true);
                this.createLowSymSplit4(ResourceCreator.LOW_SYMBOL_NAMES[i], true, true);
            }
            this.createGritaLevel1NoSplitSymbol();
            for(let i:number = 1; i < ResourceCreator.GRITA_SYMBOL_NAMES.length; i++) {
                this.createGritaNoSplitSymbols(ResourceCreator.GRITA_SYMBOL_NAMES[i]);
            }
            this.makeGretaSplitSpinTextures();

            for(let i:number = 0; i < ResourceCreator.FEATURE_SYMBOL_NAMES.length; i++) {
                this.createHorizontalSpinTextures(ResourceCreator.FEATURE_SYMBOL_NAMES[i]);
            }

            const container:PIXI.Container = new PIXI.Container();
            container.addChild(new PIXI.Sprite(TrueGrit.assetLoader.getTextures("M" + "/" + "M")[0]));
            ResourceCreator.addTextureContainer({
                container : container,
                textureName : "M" + "/" + "M" + "_spin",
                extraWidth : 0,
                filters : [ResourceCreator.verticalSpinBlurFilter]
            })

            //Almost 200 textures are created. We need to add some small delays not to choke up the browser
            const batchSize:number = 20;
            let delay:number = 0;
            const createCalls:gsap.core.Timeline = gsap.timeline({paused : true});
            const destroyCalls:gsap.core.Timeline = gsap.timeline({paused : true});
            while(ResourceCreator.textureContainers.length > 0) {
                delay += 0.01;
                let batch:ITextureContainer[] = [];
                if(ResourceCreator.textureContainers.length < batchSize) {
                    batch = ResourceCreator.textureContainers.splice(-ResourceCreator.textureContainers.length, ResourceCreator.textureContainers.length);
                } else {
                    batch = ResourceCreator.textureContainers.splice(-batchSize, batchSize);
                }

                createCalls.add(() => ResourceCreator.addBatchTextureContainersAsTextures(batch), delay);
                destroyCalls.add(() => ResourceCreator.addBatchDestroy(batch), delay);
            }

            const tl:gsap.core.Timeline = gsap.timeline({
                paused : true, onComplete : () => {
                    resolve(undefined);
                }
            });

            tl.add(createCalls.play(0));
            tl.add(destroyCalls.play(0));
            tl.play(0);
        });
    }

    private static addTextureContainer(textureContainer:ITextureContainer):void {
        ResourceCreator.textureContainers.push(textureContainer);
    }

    private static addAllTextureContainersAsTextures():void {
        let containerTexture:PIXI.Texture;
        for(let i:number = 0; i < ResourceCreator.textureContainers.length; i++) {
            ResourceCreator.textureContainers[i].container.filters = ResourceCreator.textureContainers[i].filters;
            containerTexture = TextureHelper.createRenderedTextureFromContainer(ResourceCreator.textureContainers[i].container, ResourceCreator.textureContainers[i].extraWidth);
            PIXI.Texture.addToCache(containerTexture, ResourceCreator.textureContainers[i].textureName);
        }
        for(let i:number = 0; i < ResourceCreator.textureContainers.length; i++) {
            ResourceCreator.textureContainers[i].container.destroy({children : true});
        }
    }

    private static addBatchTextureContainersAsTextures(batch:ITextureContainer[]):void {
        let containerTexture:PIXI.Texture;
        for(let i:number = 0; i < batch.length; i++) {
            batch[i].container.filters = batch[i].filters;
            containerTexture = TextureHelper.createRenderedTextureFromContainer(batch[i].container, batch[i].extraWidth);
            PIXI.Texture.addToCache(containerTexture, batch[i].textureName);
        }
    }

    private static addBatchDestroy(batch:ITextureContainer[]):void {
        for(let i:number = 0; i < batch.length; i++) {
            batch[i].container.destroy({children : true});
        }
    }

    private static createWildNoSplitTextures():void {
        const symName = "W";
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
        const splitFrameTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.WILD_SYMBOL_FRAME)[0];
        const symbolSplitBackground:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.MID_SYMBOL_BACKGROUND)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.position.set(0, 0);
        container.addChild(background01);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        container.addChild(frame);

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.position.set(9, 23);
        container.addChild(character01);

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName,
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createWildSplit2Textures():void {
        const symName = "W";
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
        const splitFrameTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.WILD_SYMBOL_FRAME)[0];
        const symbolSplitBackground:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.MID_SYMBOL_BACKGROUND)[0];
        const splitLineTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_SPLIT_LINE)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.height = Math.floor(background01.height * 0.5);
        container.addChild(background01);
        const background02:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background02.height = Math.floor(background02.height * 0.5);
        background02.position.y = (background01.position.y + background01.height) - 1;
        container.addChild(background02);

        const topCharacterPadding:number = ResourceCreator.getMidSplit2TopPadding(symName);
        const frameHeight:number = 5;

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.width = Math.floor(character01.width * 0.56);
        character01.height = Math.floor(character01.height * 0.56);
        character01.anchor.set(0.5, 0.5);
        character01.position.set(background01.width / 2, background01.position.y + (background01.height / 2) + frameHeight + 1);
        container.addChild(character01);

        const character02:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character02.width = Math.floor(character02.width * 0.56);
        character02.height = Math.floor(character02.height * 0.56);
        character02.anchor.set(0.5, 0.5);
        character02.position.set(background02.width / 2, background02.position.y + (background02.height / 2) + 1);
        container.addChild(character02);

        const splitLine:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine.width = Math.floor(splitLine.width * (120 / splitLine.width));
        splitLine.position.set(0, background02.position.y - (splitLine.height / 2));
        splitLine.tint = ResourceCreator.getSplitColor(symName);
        container.addChild(splitLine);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        container.addChild(frame);

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_02",
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_02_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_02_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createWildSplit3Textures():void {
        const symName = "W";
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
        const splitFrameTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.WILD_SYMBOL_FRAME)[0];
        const symbolSplitBackground:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.MID_SYMBOL_BACKGROUND)[0];
        const splitLineTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_SPLIT_LINE)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.height = Math.floor(background01.height * 0.34);
        container.addChild(background01);
        const background02:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background02.height = Math.floor(background02.height * 0.34);
        background02.position.y = (background01.position.y + background01.height) - 1;
        container.addChild(background02);
        const background03:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background03.height = Math.floor(background03.height * 0.34);
        background03.position.y = (background02.position.y + background02.height) - 1;
        container.addChild(background03);

        const frameHeight:number = 4;

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.width = Math.floor(character01.width * 0.34);
        character01.height = Math.floor(character01.height * 0.34);
        character01.anchor.set(0.5, 0.5);
        character01.position.set(background01.width / 2, background01.position.y + (background01.height / 2) + frameHeight);
        container.addChild(character01);

        const character02:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character02.width = Math.floor(character02.width * 0.34);
        character02.height = Math.floor(character02.height * 0.34);
        character02.anchor.set(0.5, 0.5);
        character02.position.set(background02.width / 2, background02.position.y + (background02.height / 2) + 1);
        container.addChild(character02);

        const character03:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character03.width = Math.floor(character03.width * 0.34);
        character03.height = Math.floor(character03.height * 0.34);
        character03.anchor.set(0.5, 0.5);
        character03.position.set(background03.width / 2, background03.position.y + (background03.height / 2));
        container.addChild(character03);

        const splitLine1:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine1.width = Math.floor(splitLine1.width * (120 / splitLine1.width));
        splitLine1.position.set(0, background02.position.y - (splitLine1.height / 2));
        splitLine1.tint = ResourceCreator.getSplitColor(symName);
        container.addChild(splitLine1);
        const splitLine2:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine2.width = Math.floor(splitLine2.width * (120 / splitLine2.width));
        splitLine2.position.set(0, background03.position.y - (splitLine2.height / 2));
        splitLine2.tint = ResourceCreator.getSplitColor(symName);
        container.addChild(splitLine2);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        container.addChild(frame);

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_03",
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_03_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_03_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createWildSplit4Textures():void {
        const symName = "W";
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
        const splitFrameTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.WILD_SYMBOL_FRAME)[0];
        const symbolSplitBackground:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.MID_SYMBOL_BACKGROUND)[0];
        const splitLineTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_SPLIT_LINE)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.height = Math.floor(background01.height * 0.25);
        container.addChild(background01);
        const background02:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background02.height = Math.floor(background02.height * 0.25);
        background02.position.y = (background01.position.y + background01.height) - 1;
        container.addChild(background02);
        const background03:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background03.height = Math.floor(background03.height * 0.25);
        background03.position.y = (background02.position.y + background02.height) - 1;
        container.addChild(background03);
        const background04:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background04.height = Math.floor(background04.height * 0.25);
        background04.position.y = (background03.position.y + background03.height) - 1;
        container.addChild(background04);

        const topCharacterPadding:number = ResourceCreator.getMidSplit4TopPadding(symName);
        const frameHeight:number = 4;

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.width = Math.floor(character01.width * 0.25);
        character01.height = Math.floor(character01.height * 0.25);
        character01.anchor.set(0.5, 0.5);
        character01.position.set(background01.width / 2, background01.position.y + (background01.height / 2) + frameHeight);
        container.addChild(character01);

        const character02:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character02.width = Math.floor(character02.width * 0.25);
        character02.height = Math.floor(character02.height * 0.25);
        character02.anchor.set(0.5, 0.5);
        character02.position.set(background02.width / 2, background02.position.y + (background02.height / 2) + 1);
        container.addChild(character02);

        const character03:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character03.width = Math.floor(character03.width * 0.25);
        character03.height = Math.floor(character03.height * 0.25);
        character03.anchor.set(0.5, 0.5);
        character03.position.set(background03.width / 2, background03.position.y + (background03.height / 2));
        container.addChild(character03);

        const character04:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character04.width = Math.floor(character04.width * 0.25);
        character04.height = Math.floor(character04.height * 0.25);
        character04.anchor.set(0.5, 0.5);
        character04.position.set(background04.width / 2, background04.position.y + (background04.height / 2));
        container.addChild(character04);

        const splitLine1:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine1.width = Math.floor(splitLine1.width * (120 / splitLine1.width));
        splitLine1.position.set(0, background02.position.y - (splitLine1.height / 2));
        splitLine1.tint = ResourceCreator.getSplitColor(symName);
        container.addChild(splitLine1);
        const splitLine2:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine2.width = Math.floor(splitLine2.width * (120 / splitLine2.width));
        splitLine2.position.set(0, background03.position.y - (splitLine2.height / 2));
        splitLine2.tint = ResourceCreator.getSplitColor(symName);
        container.addChild(splitLine2);
        const splitLine3:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine3.width = Math.floor(splitLine3.width * (120 / splitLine3.width));
        splitLine3.position.set(0, background04.position.y - (splitLine3.height / 2));
        splitLine3.tint = ResourceCreator.getSplitColor(symName);
        container.addChild(splitLine3);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        container.addChild(frame);

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_04",
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_04_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_04_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createHorizontalSpinTextures(symName:string):void {
        const container:PIXI.Container = new PIXI.Container();
        const symbolTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName)[0];
        container.addChild(new PIXI.Sprite(symbolTexture));

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createGritaLevel1NoSplitSymbol():void {
        const symName:string = "M0";
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.GRITA_SYMBOL_CHAR)[0];
        const splitFrameTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.GRITA_SYMBOL_FRAME)[0];
        const symbolSplitBackground:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.GRITA_SYMBOL_BACKGROUND)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.position.x = (120 - background01.width) / 2;
        background01.position.y = (120 - background01.height) / 2;
        container.addChild(background01);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        container.addChild(frame);

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.position.x = (120 - character01.width) / 2;
        character01.position.y = (120 - character01.height) / 2;
        container.addChild(character01);

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName,
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
    }

    private static createGritaNoSplitSymbols(symName:string):void {
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.GRITA_SYMBOL_CHAR)[0];
        const splitFrameTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.GRITA_SYMBOL_FRAME)[0];
        const symbolSplitBackground:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.GRITA_SYMBOL_BACKGROUND)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.position.x = (120 - background01.width) / 2;
        background01.position.y = (120 - background01.height) / 2;
        container.addChild(background01);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        frame.position.x = (120 - frame.width) / 2;
        frame.position.y = (120 - frame.height) / 2;
        container.addChild(frame);

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.position.x = (120 - character01.width) / 2;
        character01.position.y = (120 - character01.height) / 2;
        container.addChild(character01);

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName,
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
    }

    private static makeGretaSplitSpinTextures() {
        const allTheVariants = [
            "M0/M0_split_02",
            "M0/M0_split_04",
            "M0_2/M0_2_split_02",
            "M0_2/M0_2_split_04",
            "M0_3/M0_3_split_02",
            "M0_3/M0_3_split_04",
        ]
        for(let name of allTheVariants) {
            const sprite = new PIXI.Sprite(TrueGrit.assetLoader.getTextures(name)[0]);
            ResourceCreator.addTextureContainer({
                container : sprite,
                textureName : name + "_spin",
                extraWidth : 0,
                filters : [ResourceCreator.verticalSpinBlurFilter]
            })
        }

    }

    private static getFrameColor(symName:string):number {
        switch(symName) {
            case "M1" :
                return 0xfe7e7e;
            case "M2" :
                return 0xd3aaea;
            case "M3" :
                return 0x99ece7;
            case "M4" :
                return 0x99baec;
            case "M5" :
                return 0xb6ec99;
            default :
                return 0x000000;
        }
    }

    private static getSplitColor(symName:string):number {
        switch(symName) {
            case "M0_3" :
                return 0xb29621;
            case "M0_2" :
                return 0xb29621;
            case "M0" :
                return 0x80675b;
            case "M1" :
                return 0xff0000;
            case "M2" :
                return 0x8c449a;
            case "M3" :
                return 0x2f6f64;
            case "M4" :
                return 0x324474;
            case "M5" :
                return 0x517e49;
            case "W" :
                return 0x843d2d;
            default :
                return 0x000000;
        }
    }

    private static getMidNoSplitTopPadding(symName:string):PIXI.Point {
        switch(symName) {
            case "M1" :
                return new PIXI.Point(0, 5);
            case "M2" :
                return new PIXI.Point(5, 0);
            case "M3" :
                return new PIXI.Point(5, 7);
            case "M4" :
                return new PIXI.Point(8, 6);
            case "M5" :
                return new PIXI.Point(0, 0);
            default :
                return new PIXI.Point(0, 0);
        }
    }

    private static getMidSplit2TopPadding(symName:string):number {
        switch(symName) {
            case "M0" :
                return 8;
            case "M0_2" :
                return 8;
            case "M0_3" :
                return 8;
            case "M1" :
                return 24;
            case "M2" :
                return 20;
            case "M3" :
                return 26;
            case "M4" :
                return 15;
            case "M5" :
                return 10;
            case "W" :
                return 10;
            default :
                return 26;
        }
    }

    private static getMidSplit3TopPadding(symName:string):number {
        switch(symName) {
            case "M1" :
                return 18;
            case "M2" :
                return 16;
            case "M3" :
                return 26;
            case "M4" :
                return 19;
            case "M5" :
                return 15;
            case "W" :
                return -16;
            default :
                return 26;
        }
    }

    private static getMidSplit4TopPadding(symName:string):number {
        switch(symName) {
            case "M0_3" :
                return 10;
            case "M0_2" :
                return 10;
            case "M0" :
                return 10;
            case "M1" :
                return 20;
            case "M2" :
                return 19;
            case "M3" :
                return 29;
            case "M4" :
                return 22;
            case "M5" :
                return 17;
            case "W" :
                return -13;
            default :
                return 26;
        }
    }

    private static createMidSymWildTransform(symName:string):void {
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
        const splitFrameTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.WILD_SYMBOL_FRAME)[0];
        const symbolSplitBackground:PIXI.Texture = TrueGrit.assetLoader.getTextures("W/W" + ResourceCreator.MID_SYMBOL_BACKGROUND)[0];
        const wildCharTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures("W/W" + ResourceCreator.SYMBOL_CHAR)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.position = ResourceCreator.getMidNoSplitTopPadding(symName);
        container.addChild(background01);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        frame.position = ResourceCreator.getMidNoSplitTopPadding(symName);
        container.addChild(frame);

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.position.x = 3;
        if(symName == "M5") {
            character01.position.y = 3;
            character01.position.x = 10;
        }
        container.addChild(character01);

        let extraWidth:number = 3;
        if(symName == "M1" || symName == "M5") {
            extraWidth = 0;
        }

        const wildChar:PIXI.Sprite = new PIXI.Sprite(wildCharTexture);
        wildChar.position = ResourceCreator.getMidNoSplitTopPadding(symName);
        wildChar.position.x += 9;
        wildChar.position.y += 23;
        container.addChild(wildChar);

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_transformed",
            extraWidth : extraWidth,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_spin" + "_transformed",
            extraWidth : extraWidth,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_spin-horizontal" + "_transformed",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }


    private static createMidSymSplit2WildTransform(symName:string):void {
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
        const splitFrameTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.WILD_SYMBOL_FRAME)[0];
        const symbolSplitBackground:PIXI.Texture = TrueGrit.assetLoader.getTextures("W/W" + ResourceCreator.MID_SYMBOL_BACKGROUND)[0];
        const wildCharTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures("W/W" + ResourceCreator.SYMBOL_CHAR)[0];
        const splitLineTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_SPLIT_LINE)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.height = Math.floor(background01.height * 0.5);
        container.addChild(background01);
        const background02:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background02.height = Math.floor(background02.height * 0.5);
        background02.position.y = (background01.position.y + background01.height);
        container.addChild(background02);

        const topCharacterPadding:number = ResourceCreator.getMidSplit2TopPadding(symName);
        const frameHeight:number = 8;

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.position.y = -topCharacterPadding;
        character01.position.x = 3;
        if(symName == "M4") {
            character01.position.x = -4;
        }
        if(symName == "M5") {
            character01.position.x = 6;
        }
        container.addChild(character01);
        const character01Mask:PIXI.Graphics = new PIXI.Graphics();
        character01Mask.beginFill();
        character01Mask.drawRect(10, 0, 100, 52);
        character01Mask.position.y = frameHeight;
        container.addChild(character01Mask);
        character01.mask = character01Mask;

        const character02:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character02.position.y = (character01Mask.position.y + character01Mask.height) - (topCharacterPadding + frameHeight);
        character02.position.x = 3;
        if(symName == "M4") {
            character02.position.x = -4;
        }
        if(symName == "M5") {
            character02.position.x = 8;
        }
        container.addChild(character02);
        const character02Mask:PIXI.Graphics = new PIXI.Graphics();
        character02Mask.beginFill();
        character02Mask.drawRect(10, 0, 100, 52);
        character02Mask.position.y = (character01Mask.position.y + character01Mask.height);
        container.addChild(character02Mask);
        character02.mask = character02Mask;

        const splitLine:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine.position.set(0, character02Mask.position.y - (splitLine.height / 2));
        splitLine.width = Math.floor(splitLine.width * (120 / splitLine.width));
        splitLine.tint = ResourceCreator.getSplitColor("W");
        container.addChild(splitLine);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        container.addChild(frame);

        const wildCharacter01:PIXI.Sprite = new PIXI.Sprite(wildCharTexture);
        wildCharacter01.width = Math.floor(wildCharacter01.width * 0.56);
        wildCharacter01.height = Math.floor(wildCharacter01.height * 0.56);
        wildCharacter01.anchor.set(0.5, 0.5);
        wildCharacter01.position.set(background01.width / 2, background01.position.y + (background01.height / 2) + frameHeight + 1);
        container.addChild(wildCharacter01);

        const wildCharacter02:PIXI.Sprite = new PIXI.Sprite(wildCharTexture);
        wildCharacter02.width = Math.floor(wildCharacter02.width * 0.56);
        wildCharacter02.height = Math.floor(wildCharacter02.height * 0.56);
        wildCharacter02.anchor.set(0.5, 0.5);
        wildCharacter02.position.set(background02.width / 2, background02.position.y + (background02.height / 2) + 1);
        container.addChild(wildCharacter02);

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_02" + "_transformed",
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_02_spin" + "_transformed",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_02_spin-horizontal" + "_transformed",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createMidSymSplit3WildTransform(symName:string):void {
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
        const splitFrameTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.WILD_SYMBOL_FRAME)[0];
        const symbolSplitBackground:PIXI.Texture = TrueGrit.assetLoader.getTextures("W/W" + ResourceCreator.MID_SYMBOL_BACKGROUND)[0];
        const splitLineTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_SPLIT_LINE)[0];
        const wildCharTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures("W/W" + ResourceCreator.SYMBOL_CHAR)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.height = Math.floor(background01.height * 0.33);
        container.addChild(background01);
        const background02:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background02.height = (background02.height * 0.33);
        background02.position.y = Math.floor(background01.position.y + background01.height) - 1;
        container.addChild(background02);
        const background03:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background03.height = (background03.height * 0.33);
        background03.position.y = Math.floor(background02.position.y + background02.height);
        container.addChild(background03);

        const topCharacterPadding:number = ResourceCreator.getMidSplit3TopPadding(symName);
        const frameHeight:number = 7;

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.position.y = -topCharacterPadding;
        character01.position.x = 3;
        if(symName == "M4") {
            character01.position.x = -4;
        }
        if(symName == "M5") {
            character01.position.x = 8;
        }
        container.addChild(character01);
        const character01Mask:PIXI.Graphics = new PIXI.Graphics();
        character01Mask.beginFill();
        character01Mask.drawRect(10, 0, 100, 35);
        character01Mask.position.y = frameHeight;
        container.addChild(character01Mask);
        character01.mask = character01Mask;

        const character02:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character02.position.y = (character01Mask.position.y + character01Mask.height) - (topCharacterPadding + frameHeight);
        character02.position.x = 3;
        if(symName == "M4") {
            character02.position.x = -4;
        }
        if(symName == "M5") {
            character02.position.x = 8;
        }
        container.addChild(character02);
        const character02Mask:PIXI.Graphics = new PIXI.Graphics();
        character02Mask.beginFill();
        character02Mask.drawRect(10, 0, 100, 35);
        character02Mask.position.y = (character01Mask.position.y + character01Mask.height);
        container.addChild(character02Mask);
        character02.mask = character02Mask;

        const character03:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character03.position.y = (character02Mask.position.y + character02Mask.height) - (topCharacterPadding + frameHeight);
        character03.position.x = 3;
        if(symName == "M4") {
            character03.position.x = -4;
        }
        if(symName == "M5") {
            character03.position.x = 8;
        }
        container.addChild(character03);
        const character03Mask:PIXI.Graphics = new PIXI.Graphics();
        character03Mask.beginFill();
        character03Mask.drawRect(10, 0, 100, 34);
        character03Mask.position.y = (character02Mask.position.y + character02Mask.height);
        container.addChild(character03Mask);
        character03.mask = character03Mask;

        const splitLine1:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine1.position.set(0, character02Mask.position.y - (splitLine1.height / 2));
        splitLine1.tint = ResourceCreator.getSplitColor("W");
        splitLine1.width = Math.floor(splitLine1.width * (120 / splitLine1.width));
        container.addChild(splitLine1);
        const splitLine2:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine2.position.set(0, character03Mask.position.y - (splitLine2.height / 2));
        splitLine2.tint = ResourceCreator.getSplitColor("W");
        splitLine2.width = Math.floor(splitLine2.width * (120 / splitLine2.width));
        container.addChild(splitLine2);

        const wildCharacter01:PIXI.Sprite = new PIXI.Sprite(wildCharTexture);
        wildCharacter01.width = Math.floor(wildCharacter01.width * 0.34);
        wildCharacter01.height = Math.floor(wildCharacter01.height * 0.34);
        wildCharacter01.anchor.set(0.5, 0.5);
        wildCharacter01.position.set(background01.width / 2, background01.position.y + (background01.height / 2) + frameHeight);
        container.addChild(wildCharacter01);

        const wildCharacter02:PIXI.Sprite = new PIXI.Sprite(wildCharTexture);
        wildCharacter02.width = Math.floor(wildCharacter02.width * 0.34);
        wildCharacter02.height = Math.floor(wildCharacter02.height * 0.34);
        wildCharacter02.anchor.set(0.5, 0.5);
        wildCharacter02.position.set(background02.width / 2, background02.position.y + (background02.height / 2) + 5);
        container.addChild(wildCharacter02);

        const wildCharacter03:PIXI.Sprite = new PIXI.Sprite(wildCharTexture);
        wildCharacter03.width = Math.floor(wildCharacter03.width * 0.34);
        wildCharacter03.height = Math.floor(wildCharacter03.height * 0.34);
        wildCharacter03.anchor.set(0.5, 0.5);
        wildCharacter03.position.set(background03.width / 2, background03.position.y + (background03.height / 2) + 2);
        container.addChild(wildCharacter03);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        container.addChild(frame);

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_03" + "_transformed",
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_03_spin" + "_transformed",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_03_spin-horizontal" + "_transformed",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createMidSymSplit4WildTransform(symName:string):void {
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
        const splitFrameTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.WILD_SYMBOL_FRAME)[0];
        const symbolSplitBackground:PIXI.Texture = TrueGrit.assetLoader.getTextures("W/W" + ResourceCreator.MID_SYMBOL_BACKGROUND)[0];
        const splitLineTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_SPLIT_LINE)[0];
        const wildCharTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures("W/W" + ResourceCreator.SYMBOL_CHAR)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.height = Math.floor(background01.height * 0.25);
        container.addChild(background01);
        const background02:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background02.height = Math.floor(background02.height * 0.25);
        background02.position.y = (background01.position.y + background01.height) - 1;
        container.addChild(background02);
        const background03:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background03.height = Math.floor(background03.height * 0.25);
        background03.position.y = (background02.position.y + background02.height);
        container.addChild(background03);
        const background04:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background04.height = Math.floor(background04.height * 0.25);
        background04.position.y = (background03.position.y + background03.height) - 1;
        container.addChild(background04);

        const topCharacterPadding:number = ResourceCreator.getMidSplit4TopPadding(symName);
        const frameHeight:number = 6;

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.position.y = -topCharacterPadding;
        character01.position.x = 3;
        if(symName == "M4") {
            character01.position.x = -4;
        }
        if(symName == "M5") {
            character01.position.x = 8;
        }
        container.addChild(character01);
        const character01Mask:PIXI.Graphics = new PIXI.Graphics();
        character01Mask.beginFill();
        character01Mask.drawRect(10, 0, 100, 26);
        character01Mask.position.y = frameHeight;
        container.addChild(character01Mask);
        character01.mask = character01Mask;

        const character02:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character02.position.y = (character01Mask.position.y + character01Mask.height) - (topCharacterPadding + frameHeight);
        character02.position.x = 3;
        if(symName == "M4") {
            character02.position.x = -4;
        }
        if(symName == "M5") {
            character02.position.x = 8;
        }
        container.addChild(character02);
        const character02Mask:PIXI.Graphics = new PIXI.Graphics();
        character02Mask.beginFill();
        character02Mask.drawRect(10, 0, 100, 26);
        character02Mask.position.y = (character01Mask.position.y + character01Mask.height);
        container.addChild(character02Mask);
        character02.mask = character02Mask;

        const character03:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character03.position.y = (character02Mask.position.y + character02Mask.height) - (topCharacterPadding + frameHeight);
        character03.position.x = 3;
        if(symName == "M4") {
            character03.position.x = -4;
        }
        if(symName == "M5") {
            character03.position.x = 8;
        }
        container.addChild(character03);
        const character03Mask:PIXI.Graphics = new PIXI.Graphics();
        character03Mask.beginFill();
        character03Mask.drawRect(10, 0, 100, 26);
        character03Mask.position.y = (character02Mask.position.y + character02Mask.height);
        container.addChild(character03Mask);
        character03.mask = character03Mask;

        const character04:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character04.position.y = (character03Mask.position.y + character03Mask.height) - (topCharacterPadding + frameHeight);
        character04.position.x = 3;
        if(symName == "M4") {
            character04.position.x = -4;
        }
        if(symName == "M5") {
            character04.position.x = 8;
        }
        container.addChild(character04);
        const character04Mask:PIXI.Graphics = new PIXI.Graphics();
        character04Mask.beginFill();
        character04Mask.drawRect(10, 0, 100, 27);
        character04Mask.position.y = (character03Mask.position.y + character03Mask.height);
        container.addChild(character04Mask);
        character04.mask = character04Mask;

        const splitLine1:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine1.position.set(0, character02Mask.position.y - (splitLine1.height / 2));
        splitLine1.width = Math.floor(splitLine1.width * (120 / splitLine1.width));
        splitLine1.tint = ResourceCreator.getSplitColor("W");
        container.addChild(splitLine1);
        const splitLine2:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine2.position.set(0, character03Mask.position.y - (splitLine2.height / 2));
        splitLine2.width = Math.floor(splitLine1.width * (120 / splitLine1.width));
        splitLine2.tint = ResourceCreator.getSplitColor("W");
        container.addChild(splitLine2);
        const splitLine3:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine3.position.set(0, character04Mask.position.y - (splitLine3.height / 2));
        splitLine3.width = Math.floor(splitLine1.width * (120 / splitLine1.width));
        splitLine3.tint = ResourceCreator.getSplitColor("W");
        container.addChild(splitLine3);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        container.addChild(frame);

        const wildCharacter01:PIXI.Sprite = new PIXI.Sprite(wildCharTexture);
        wildCharacter01.width = Math.floor(wildCharacter01.width * 0.25);
        wildCharacter01.height = Math.floor(wildCharacter01.height * 0.25);
        wildCharacter01.anchor.set(0.5, 0.5);
        wildCharacter01.position.set(background01.width / 2, background01.position.y + (background01.height / 2) + frameHeight);
        container.addChild(wildCharacter01);

        const wildCharacter02:PIXI.Sprite = new PIXI.Sprite(wildCharTexture);
        wildCharacter02.width = Math.floor(wildCharacter02.width * 0.25);
        wildCharacter02.height = Math.floor(wildCharacter02.height * 0.25);
        wildCharacter02.anchor.set(0.5, 0.5);
        wildCharacter02.position.set(background02.width / 2, background02.position.y + (background02.height / 2) + 4);
        container.addChild(wildCharacter02);

        const wildCharacter03:PIXI.Sprite = new PIXI.Sprite(wildCharTexture);
        wildCharacter03.width = Math.floor(wildCharacter03.width * 0.25);
        wildCharacter03.height = Math.floor(wildCharacter03.height * 0.25);
        wildCharacter03.anchor.set(0.5, 0.5);
        wildCharacter03.position.set(background03.width / 2, background03.position.y + (background03.height / 2));
        container.addChild(wildCharacter03);

        const wildCharacter04:PIXI.Sprite = new PIXI.Sprite(wildCharTexture);
        wildCharacter04.width = Math.floor(wildCharacter04.width * 0.25);
        wildCharacter04.height = Math.floor(wildCharacter04.height * 0.25);
        wildCharacter04.anchor.set(0.5, 0.5);
        wildCharacter04.position.set(background04.width / 2, background04.position.y + (background04.height / 2) - 2);
        container.addChild(wildCharacter04);

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_04" + "_transformed",
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_04_spin" + "_transformed",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_04_spin-horizontal" + "_transformed",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createMidSymNoSplit(symName:string, gold:boolean = false, black:boolean = false):void {
        const container:PIXI.Container = new PIXI.Container();
        let charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
        let splitFrameTexture:PIXI.Texture;
        let symbolSplitBackground:PIXI.Texture;

        if(gold) {
            splitFrameTexture = TrueGrit.assetLoader.getTextures("T/T1_frame")[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures("T/T1_bg")[0];
        } else {
            splitFrameTexture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_FRAME)[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.MID_SYMBOL_BACKGROUND)[0];
        }


        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.position = ResourceCreator.getMidNoSplitTopPadding(symName);
        background01.position.x += 5;
        background01.position.y += 8;
        container.addChild(background01);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        if(!gold) {
            frame.tint = ResourceCreator.getFrameColor(symName);
        }
        frame.position = ResourceCreator.getMidNoSplitTopPadding(symName);
        container.addChild(frame);

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.position.x = 3;
        if(symName == "M5") {
            character01.position.y = 3;
            character01.position.x = 10;
        }
        container.addChild(character01);

        if(black) {
            character01.tint = 0x000000;
        }

        let extraWidth:number = 3;
        if(symName == "M1" || symName == "M5") {
            extraWidth = 0;
        }


        if(gold) {
            let suffix = "_gold";
            if(black) {
                suffix += "_black";
            }
            ResourceCreator.addTextureContainer({
                container : container,
                textureName : symName + "/" + symName + suffix,
                extraWidth : extraWidth,
                filters : []
            })
            return;
        }

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName,
            extraWidth : extraWidth,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_spin",
            extraWidth : extraWidth,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createMidSymSplit2(symName:string, gold:boolean = false, black:boolean = false):void {
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
        let splitFrameTexture:PIXI.Texture;
        let symbolSplitBackground:PIXI.Texture;

        if(gold) {
            splitFrameTexture = TrueGrit.assetLoader.getTextures("T/T1_frame")[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures("T/T1_bg")[0];
        } else {
            splitFrameTexture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_FRAME)[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.MID_SYMBOL_BACKGROUND)[0];
        }

        const splitLineTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_SPLIT_LINE)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.height = Math.floor(background01.height * 0.5);
        background01.position.set(5, 8);
        container.addChild(background01);
        const background02:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background02.height = Math.floor(background02.height * 0.5);
        background02.position.x = 5;
        background02.position.y = (background01.position.y + background01.height);
        container.addChild(background02);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        if(!gold) {
            frame.tint = ResourceCreator.getFrameColor(symName);
        }
        container.addChild(frame);

        const topCharacterPadding:number = ResourceCreator.getMidSplit2TopPadding(symName);
        const frameHeight:number = 10;

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.position.y = -topCharacterPadding;
        character01.position.x = 3;
        if(symName == "M4") {
            character01.position.x = -4;
        }
        if(symName == "M5") {
            character01.position.x = 8;
        }
        container.addChild(character01);
        const character01Mask:PIXI.Graphics = new PIXI.Graphics();
        character01Mask.beginFill();
        character01Mask.drawRect(10, 0, 100, 50);
        character01Mask.position.y = frameHeight;
        container.addChild(character01Mask);
        character01.mask = character01Mask;

        const character02:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character02.position.y = (character01Mask.position.y + character01Mask.height) - (topCharacterPadding + frameHeight);
        character02.position.x = 3;
        if(symName == "M4") {
            character02.position.x = -4;
        }
        if(symName == "M5") {
            character02.position.x = 8;
        }
        container.addChild(character02);
        const character02Mask:PIXI.Graphics = new PIXI.Graphics();
        character02Mask.beginFill();
        character02Mask.drawRect(10, 0, 100, 50);
        character02Mask.position.y = (character01Mask.position.y + character01Mask.height);
        container.addChild(character02Mask);
        character02.mask = character02Mask;

        if(black) {
            character01.tint = 0x000000;
            character02.tint = 0x000000;
        }

        const splitLine:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine.position.set(10, character02Mask.position.y - (splitLine.height / 2));
        if(gold) {
            splitLine.tint = 0xaf850c;
        } else {
            splitLine.tint = ResourceCreator.getSplitColor(symName);
        }
        container.addChild(splitLine);


        if(gold) {
            let suffix = "_gold";
            if(black) {
                suffix += "_black";
            }
            ResourceCreator.addTextureContainer({
                container : container,
                textureName : symName + "/" + symName + "_split_02" + suffix,
                extraWidth : 0,
                filters : []
            })
            return;
        }

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_02",
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_02_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_02_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createMidSymSplit3(symName:string, gold:boolean = false, black:boolean = false):void {
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
        let splitFrameTexture:PIXI.Texture;
        let symbolSplitBackground:PIXI.Texture;

        if(gold) {
            splitFrameTexture = TrueGrit.assetLoader.getTextures("T/T1_frame")[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures("T/T1_bg")[0];
        } else {
            splitFrameTexture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_FRAME)[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.MID_SYMBOL_BACKGROUND)[0];
        }

        const splitLineTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_SPLIT_LINE)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.height = Math.floor(background01.height * 0.33);
        background01.position.set(5, 8);
        container.addChild(background01);
        const background02:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background02.height = (background02.height * 0.33);
        background02.position.x = 5;
        background02.position.y = Math.floor(background01.position.y + background01.height);
        container.addChild(background02);
        const background03:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background03.height = (background03.height * 0.33);
        background03.position.x = 5;
        background03.position.y = Math.floor(background02.position.y + background02.height);
        container.addChild(background03);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        if(!gold) {
            frame.tint = ResourceCreator.getFrameColor(symName);
        }
        container.addChild(frame);

        const topCharacterPadding:number = ResourceCreator.getMidSplit3TopPadding(symName);
        const frameHeight:number = 10;

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.position.y = -topCharacterPadding;
        character01.position.x = 3;
        if(symName == "M4") {
            character01.position.x = -4;
        }
        if(symName == "M5") {
            character01.position.x = 8;
        }
        container.addChild(character01);
        const character01Mask:PIXI.Graphics = new PIXI.Graphics();
        character01Mask.beginFill();
        character01Mask.drawRect(10, 0, 100, 34);
        character01Mask.position.y = frameHeight;
        container.addChild(character01Mask);
        character01.mask = character01Mask;

        const character02:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character02.position.y = (character01Mask.position.y + character01Mask.height) - (topCharacterPadding + frameHeight);
        character02.position.x = 3;
        if(symName == "M4") {
            character02.position.x = -4;
        }
        if(symName == "M5") {
            character02.position.x = 8;
        }
        container.addChild(character02);
        const character02Mask:PIXI.Graphics = new PIXI.Graphics();
        character02Mask.beginFill();
        character02Mask.drawRect(10, 0, 100, 34);
        character02Mask.position.y = (character01Mask.position.y + character01Mask.height);
        container.addChild(character02Mask);
        character02.mask = character02Mask;

        const character03:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character03.position.y = (character02Mask.position.y + character02Mask.height) - (topCharacterPadding + frameHeight);
        character03.position.x = 3;
        if(symName == "M4") {
            character03.position.x = -4;
        }
        if(symName == "M5") {
            character03.position.x = 8;
        }
        container.addChild(character03);
        const character03Mask:PIXI.Graphics = new PIXI.Graphics();
        character03Mask.beginFill();
        character03Mask.drawRect(10, 0, 100, 33);
        character03Mask.position.y = (character02Mask.position.y + character02Mask.height);
        container.addChild(character03Mask);
        character03.mask = character03Mask;

        if(black) {
            character01.tint = 0x000000;
            character02.tint = 0x000000;
            character03.tint = 0x000000;
        }


        const splitLine1:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine1.position.set(10, character02Mask.position.y - (splitLine1.height / 2));
        container.addChild(splitLine1);
        const splitLine2:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine2.position.set(10, character03Mask.position.y - (splitLine2.height / 2));
        container.addChild(splitLine2);

        if(gold) {
            splitLine1.tint = 0xaf850c;
            splitLine2.tint = 0xaf850c;
        } else {
            splitLine1.tint = ResourceCreator.getSplitColor(symName);
            splitLine2.tint = ResourceCreator.getSplitColor(symName);
        }


        if(gold) {
            let suffix = "_gold";
            if(black) {
                suffix += "_black";
            }
            ResourceCreator.addTextureContainer({
                container : container,
                textureName : symName + "/" + symName + "_split_03" + suffix,
                extraWidth : 0,
                filters : []
            })
            return;
        }
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_03",
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_03_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_03_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createMidSymSplit4(symName:string, gold:boolean = false, black:boolean = false):void {
        const container:PIXI.Container = new PIXI.Container();
        const charTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
        let splitFrameTexture:PIXI.Texture;
        let symbolSplitBackground:PIXI.Texture;

        if(gold) {
            splitFrameTexture = TrueGrit.assetLoader.getTextures("T/T1_frame")[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures("T/T1_bg")[0];
        } else {
            splitFrameTexture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_FRAME)[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.MID_SYMBOL_BACKGROUND)[0];
        }

        const splitLineTexture:PIXI.Texture = TrueGrit.assetLoader.getTextures(ResourceCreator.MID_SYMBOL_SPLIT_LINE)[0];

        const background01:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background01.height = Math.floor(background01.height * 0.25);
        background01.position.set(5, 8);
        container.addChild(background01);
        const background02:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background02.height = Math.floor(background02.height * 0.25);
        background02.position.x = 5;
        background02.position.y = (background01.position.y + background01.height);
        container.addChild(background02);
        const background03:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background03.height = Math.floor(background03.height * 0.25);
        background03.position.x = 5;
        background03.position.y = (background02.position.y + background02.height);
        container.addChild(background03);
        const background04:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        background04.height = Math.floor(background04.height * 0.25);
        background04.position.x = 5;
        background04.position.y = (background03.position.y + background03.height);
        container.addChild(background04);

        const frame:PIXI.Sprite = new PIXI.Sprite(splitFrameTexture);
        if(!gold) {
            frame.tint = ResourceCreator.getFrameColor(symName);
        }
        container.addChild(frame);

        const topCharacterPadding:number = ResourceCreator.getMidSplit4TopPadding(symName);
        const frameHeight:number = 10;

        const character01:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character01.position.y = -topCharacterPadding;
        character01.position.x = 3;
        if(symName == "M4") {
            character01.position.x = -4;
        }
        if(symName == "M5") {
            character01.position.x = 8;
        }
        container.addChild(character01);
        const character01Mask:PIXI.Graphics = new PIXI.Graphics();
        character01Mask.beginFill();
        character01Mask.drawRect(10, 0, 100, 25);
        character01Mask.position.y = frameHeight;
        container.addChild(character01Mask);
        character01.mask = character01Mask;

        const character02:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character02.position.y = (character01Mask.position.y + character01Mask.height) - (topCharacterPadding + frameHeight);
        character02.position.x = 3;
        if(symName == "M4") {
            character02.position.x = -4;
        }
        if(symName == "M5") {
            character02.position.x = 8;
        }
        container.addChild(character02);
        const character02Mask:PIXI.Graphics = new PIXI.Graphics();
        character02Mask.beginFill();
        character02Mask.drawRect(10, 0, 100, 25);
        character02Mask.position.y = (character01Mask.position.y + character01Mask.height);
        container.addChild(character02Mask);
        character02.mask = character02Mask;

        const character03:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character03.position.y = (character02Mask.position.y + character02Mask.height) - (topCharacterPadding + frameHeight);
        character03.position.x = 3;
        if(symName == "M4") {
            character03.position.x = -4;
        }
        if(symName == "M5") {
            character03.position.x = 8;
        }
        container.addChild(character03);
        const character03Mask:PIXI.Graphics = new PIXI.Graphics();
        character03Mask.beginFill();
        character03Mask.drawRect(10, 0, 100, 25);
        character03Mask.position.y = (character02Mask.position.y + character02Mask.height);
        container.addChild(character03Mask);
        character03.mask = character03Mask;

        const character04:PIXI.Sprite = new PIXI.Sprite(charTexture);
        character04.position.y = (character03Mask.position.y + character03Mask.height) - (topCharacterPadding + frameHeight);
        character04.position.x = 3;
        if(symName == "M4") {
            character04.position.x = -4;
        }
        if(symName == "M5") {
            character04.position.x = 8;
        }
        container.addChild(character04);
        const character04Mask:PIXI.Graphics = new PIXI.Graphics();
        character04Mask.beginFill();
        character04Mask.drawRect(10, 0, 100, 26);
        character04Mask.position.y = (character03Mask.position.y + character03Mask.height);
        container.addChild(character04Mask);
        character04.mask = character04Mask;


        if(black) {
            character01.tint = 0x000000;
            character02.tint = 0x000000;
            character03.tint = 0x000000;
            character04.tint = 0x000000;
        }

        const splitLine1:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine1.position.set(10, character02Mask.position.y - (splitLine1.height / 2));
        splitLine1.tint = ResourceCreator.getSplitColor(symName);
        container.addChild(splitLine1);
        const splitLine2:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine2.position.set(10, character03Mask.position.y - (splitLine2.height / 2));
        container.addChild(splitLine2);
        const splitLine3:PIXI.Sprite = new PIXI.Sprite(splitLineTexture);
        splitLine3.position.set(10, character04Mask.position.y - (splitLine3.height / 2));
        container.addChild(splitLine3);


        if(gold) {
            splitLine1.tint = 0xaf850c;
            splitLine2.tint = 0xaf850c;
            splitLine3.tint = 0xaf850c;
        } else {
            splitLine1.tint = ResourceCreator.getSplitColor(symName);
            splitLine2.tint = ResourceCreator.getSplitColor(symName);
            splitLine3.tint = ResourceCreator.getSplitColor(symName);
        }

        if(gold) {
            let suffix = "_gold";
            if(black) {
                suffix += "_black";
            }
            ResourceCreator.addTextureContainer({
                container : container,
                textureName : symName + "/" + symName + "_split_04" + suffix,
                extraWidth : 0,
                filters : []
            })
            return;
        }

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_04",
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_04_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_04_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createLowSymNoSplit(symName:string, gold:boolean = false, black:boolean = false):void {
        const container:PIXI.Container = new PIXI.Container();
        let splitCharTexture:PIXI.Texture;
        let symbolSplitBackground:PIXI.Texture;

        if(gold) {
            splitCharTexture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR + "T2")[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures("T/T2bg")[0];
        } else {
            splitCharTexture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures(symName + "/" + symName + "bg")[0];
        }

        const splitPart1:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        container.addChild(splitPart1);
        const splitChar01:PIXI.Sprite = new PIXI.Sprite(splitCharTexture);
        splitChar01.anchor.set(0.5, 0);
        splitChar01.position.x = (splitPart1.width / 2);
        container.addChild(splitChar01);

        if(black) {
            splitChar01.tint = 0x000000;
        }

        if(gold) {
            let suffix = "_gold";
            if(black) {
                suffix += "_black";
            }
            ResourceCreator.addTextureContainer({
                container : container,
                textureName : symName + "/" + symName + suffix,
                extraWidth : 0,
                filters : []
            })
            return;
        }

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName,
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createLowSymSplit2(symName:string, gold:boolean = false, black:boolean = false):void {
        const container:PIXI.Container = new PIXI.Container();

        let splitCharTexture:PIXI.Texture;
        let symbolSplitBackground:PIXI.Texture;

        if(gold) {
            splitCharTexture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR + "T2")[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures("T/T2bg_split")[0];
        } else {
            splitCharTexture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures(ResourceCreator.LOW_SYMBOL_SPLIT_BACKGROUND)[0];
        }


        const splitPart1:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        container.addChild(splitPart1);
        const splitChar01:PIXI.Sprite = new PIXI.Sprite(splitCharTexture);
        splitChar01.width = Math.floor(splitChar01.width * 0.5);
        splitChar01.height = Math.floor(splitChar01.height * 0.5);
        splitChar01.anchor.set(0.5, 0);
        splitChar01.position.set(splitPart1.width / 2, 0);
        container.addChild(splitChar01);
        splitChar01.filters = [this.lowSymShadowFilter];

        const splitPart2:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        splitPart2.position.y = splitPart1.height - 1;
        container.addChild(splitPart2);
        const splitChar02:PIXI.Sprite = new PIXI.Sprite(splitCharTexture);
        splitChar02.width = Math.floor(splitChar02.width * 0.5);
        splitChar02.height = Math.floor(splitChar02.height * 0.5);
        splitChar02.anchor.set(0.5, 0);
        splitChar02.position.set(splitPart2.width / 2, splitPart2.position.y);
        container.addChild(splitChar02);
        splitChar02.filters = [this.lowSymShadowFilter];


        if(black) {
            splitChar01.tint = 0x000000;
            splitChar02.tint = 0x000000;
        }

        if(gold) {
            let suffix = "_gold";
            if(black) {
                suffix += "_black";
            }
            ResourceCreator.addTextureContainer({
                container : container,
                textureName : symName + "/" + symName + "_split_02" + suffix,
                extraWidth : 0,
                filters : []
            })
            return;
        }

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_02",
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_02_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_02_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createLowSymSplit3(symName:string, gold:boolean = false, black:boolean = false):void {
        const container:PIXI.Container = new PIXI.Container();

        let splitCharTexture:PIXI.Texture;
        let symbolSplitBackground:PIXI.Texture;

        if(gold) {
            splitCharTexture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR + "T2")[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures("T/T2bg_split")[0];
        } else {
            splitCharTexture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures(ResourceCreator.LOW_SYMBOL_SPLIT_BACKGROUND)[0];
        }

        const splitPart1:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        splitPart1.height = Math.floor(splitPart1.height * 0.66);
        container.addChild(splitPart1);
        const splitChar01:PIXI.Sprite = new PIXI.Sprite(splitCharTexture);
        splitChar01.width = Math.floor(splitChar01.width * 0.35);
        splitChar01.height = Math.floor(splitChar01.height * 0.35);
        splitChar01.anchor.set(0.5, 0);
        splitChar01.position.set(splitPart1.width / 2, -2);
        container.addChild(splitChar01);
        splitChar01.filters = [this.lowSymShadowFilter];

        const splitPart2:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        splitPart2.height = Math.floor(splitPart2.height * 0.66);
        splitPart2.position.y = splitPart1.height - 1;
        container.addChild(splitPart2);
        const splitChar02:PIXI.Sprite = new PIXI.Sprite(splitCharTexture);
        splitChar02.width = Math.floor(splitChar02.width * 0.35);
        splitChar02.height = Math.floor(splitChar02.height * 0.35);
        splitChar02.anchor.set(0.5, 0);
        splitChar02.position.set(splitPart2.width / 2, splitPart2.position.y - 2);
        container.addChild(splitChar02);
        splitChar02.filters = [this.lowSymShadowFilter];

        const splitPart3:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        splitPart3.height = Math.floor(splitPart3.height * 0.66);
        splitPart3.position.y = (splitPart2.position.y + splitPart2.height) - 1;
        container.addChild(splitPart3);
        const splitChar03:PIXI.Sprite = new PIXI.Sprite(splitCharTexture);
        splitChar03.width = Math.floor(splitChar03.width * 0.35);
        splitChar03.height = Math.floor(splitChar03.height * 0.35);
        splitChar03.anchor.set(0.5, 0);
        splitChar03.position.set(splitPart3.width / 2, splitPart3.position.y - 2);
        container.addChild(splitChar03);
        splitChar03.filters = [this.lowSymShadowFilter];


        if(black) {
            splitChar01.tint = 0x000000;
            splitChar02.tint = 0x000000;
            splitChar03.tint = 0x000000;
        }

        if(gold) {
            let suffix = "_gold";
            if(black) {
                suffix += "_black";
            }
            ResourceCreator.addTextureContainer({
                container : container,
                textureName : symName + "/" + symName + "_split_03" + suffix,
                extraWidth : 0,
                filters : []
            })
            return;
        }

        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_03",
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_03_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_03_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static createLowSymSplit4(symName:string, gold:boolean = false, black:boolean = false):void {
        const container:PIXI.Container = new PIXI.Container();

        let splitCharTexture:PIXI.Texture;
        let symbolSplitBackground:PIXI.Texture;

        if(gold) {
            splitCharTexture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR + "T2")[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures("T/T2bg_split")[0];
        } else {
            splitCharTexture = TrueGrit.assetLoader.getTextures(symName + "/" + symName + ResourceCreator.SYMBOL_CHAR)[0];
            symbolSplitBackground = TrueGrit.assetLoader.getTextures(ResourceCreator.LOW_SYMBOL_SPLIT_BACKGROUND)[0];
        }


        const splitPart1:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        splitPart1.height = Math.floor(splitPart1.height * 0.5);
        container.addChild(splitPart1);
        const splitChar01:PIXI.Sprite = new PIXI.Sprite(splitCharTexture);
        splitChar01.width = Math.floor(splitChar01.width * 0.27);
        splitChar01.height = Math.floor(splitChar01.height * 0.27);
        splitChar01.anchor.set(0.5, 0);
        splitChar01.position.set(splitPart1.width / 2, -2);
        container.addChild(splitChar01);
        splitChar01.filters = [this.lowSymShadowFilter];

        const splitPart2:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        splitPart2.height = Math.floor(splitPart2.height * 0.5);
        splitPart2.position.y = splitPart1.height - 1;
        container.addChild(splitPart2);
        const splitChar02:PIXI.Sprite = new PIXI.Sprite(splitCharTexture);
        splitChar02.width = Math.floor(splitChar02.width * 0.27);
        splitChar02.height = Math.floor(splitChar02.height * 0.27);
        splitChar02.anchor.set(0.5, 0);
        splitChar02.position.set(splitPart2.width / 2, splitPart2.position.y - 2);
        container.addChild(splitChar02);
        splitChar02.filters = [this.lowSymShadowFilter];

        const splitPart3:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        splitPart3.height = Math.floor(splitPart3.height * 0.5);
        splitPart3.position.y = (splitPart2.position.y + splitPart2.height) - 1;
        container.addChild(splitPart3);
        const splitChar03:PIXI.Sprite = new PIXI.Sprite(splitCharTexture);
        splitChar03.width = Math.floor(splitChar03.width * 0.27);
        splitChar03.height = Math.floor(splitChar03.height * 0.27);
        splitChar03.anchor.set(0.5, 0);
        splitChar03.position.set(splitPart3.width / 2, splitPart3.position.y - 2);
        container.addChild(splitChar03);
        splitChar03.filters = [this.lowSymShadowFilter];

        const splitPart4:PIXI.Sprite = new PIXI.Sprite(symbolSplitBackground);
        splitPart4.height = Math.floor(splitPart4.height * 0.5);
        splitPart4.position.y = (splitPart3.position.y + splitPart3.height) - 1;
        container.addChild(splitPart4);
        const splitChar04:PIXI.Sprite = new PIXI.Sprite(splitCharTexture);
        splitChar04.width = Math.floor(splitChar04.width * 0.27);
        splitChar04.height = Math.floor(splitChar04.height * 0.27);
        splitChar04.anchor.set(0.5, 0);
        splitChar04.position.set(splitPart4.width / 2, splitPart4.position.y - 1);
        container.addChild(splitChar04);
        splitChar04.filters = [this.lowSymShadowFilter];


        if(black) {
            splitChar01.tint = 0x000000;
            splitChar02.tint = 0x000000;
            splitChar03.tint = 0x000000;
            splitChar04.tint = 0x000000;
        }

        if(gold) {
            let suffix = "_gold";
            if(black) {
                suffix += "_black";
            }
            ResourceCreator.addTextureContainer({
                container : container,
                textureName : symName + "/" + symName + "_split_04" + suffix,
                extraWidth : 0,
                filters : []
            })
            return;
        }


        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_04",
            extraWidth : 0,
            filters : []
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_04_spin",
            extraWidth : 0,
            filters : [ResourceCreator.verticalSpinBlurFilter]
        })
        ResourceCreator.addTextureContainer({
            container : container,
            textureName : symName + "/" + symName + "_split_04_spin-horizontal",
            extraWidth : 0,
            filters : [ResourceCreator.horizontalSpinBlurFilter]
        })
    }

    private static updateTextureCache(texture:PIXI.Texture, id:string):void {
        PIXI.Texture.addToCache(texture, id);
    }
}